import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { analyticsOffersService } from "@/services/analytics-offers-service";
import type { OfferAnalyticsSurfaceApi } from "@/types/analytics-offers";
import type { DateRange } from "@/hooks/useDateRange";
import type { OffersStage } from "../components/offers/types";
import { useAnalyticsOverviewRange } from "./useAnalyticsOverviewRange";
import {
  getPreviousAnalyticsDateRange,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";

export const STAGE_TO_SURFACE: Record<OffersStage, OfferAnalyticsSurfaceApi> = {
  preCheckIn: "PRE_CHECK_IN",
  dashboardCheckIn: "DASHBOARD_CHECK_IN",
  dashboardStay: "DASHBOARD_IN_STAY",
  dashboardCheckOut: "DASHBOARD_CHECK_OUT",
  preCheckOut: "PRE_CHECK_OUT",
};

export const analyticsOffersKeys = {
  all: ["analytics-offers"] as const,
  surface: (
    hotelId: string,
    from: string,
    to: string,
    surface: OfferAnalyticsSurfaceApi,
  ) =>
    [
      ...analyticsOffersKeys.all,
      "surface",
      hotelId,
      from,
      to,
      surface,
    ] as const,
  roomUpgradeSummary: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOffersKeys.all,
      "room-upgrade-summary",
      hotelId,
      from,
      to,
    ] as const,
  revenueTrend: (hotelId: string, from: string, to: string) =>
    [
      ...analyticsOffersKeys.all,
      "room-upgrade-revenue-trend",
      hotelId,
      from,
      to,
    ] as const,
};

export function useOffersAnalytics(dateRange: DateRange, stage: OffersStage) {
  const rangeParams = useAnalyticsOverviewRange(dateRange);
  const { hotelId, from, to, isReady } = rangeParams;
  const surface = STAGE_TO_SURFACE[stage];
  const fetchRevenueTrend = stage === "preCheckIn";
  const previousRange = useMemo(
    () => getPreviousAnalyticsDateRange(dateRange),
    [dateRange],
  );
  const previousFrom = toAnalyticsDateParam(previousRange?.from);
  const previousTo = toAnalyticsDateParam(previousRange?.to);
  const hasPreviousRange = Boolean(previousFrom && previousTo);

  const surfaceTab = useQuery({
    queryKey: analyticsOffersKeys.surface(hotelId, from, to, surface),
    queryFn: () =>
      analyticsOffersService.getSurfaceTab({
        hotelId,
        from,
        to,
        surface,
      }),
    enabled: isReady,
  });

  const revenueTrend = useQuery({
    queryKey: analyticsOffersKeys.revenueTrend(hotelId, from, to),
    queryFn: () =>
      analyticsOffersService.getRoomUpgradeRevenueTrend({
        hotelId,
        from,
        to,
      }),
    enabled: isReady && fetchRevenueTrend,
  });

  const previousRoomUpgradeSummary = useQuery({
    queryKey: analyticsOffersKeys.roomUpgradeSummary(
      hotelId,
      previousFrom ?? "",
      previousTo ?? "",
    ),
    queryFn: () =>
      analyticsOffersService.getRoomUpgradeSummary({
        hotelId,
        from: previousFrom ?? "",
        to: previousTo ?? "",
      }),
    enabled: isReady && hasPreviousRange && fetchRevenueTrend,
  });

  return {
    ...rangeParams,
    surface,
    previousRange,
    surfaceTab,
    previousRoomUpgradeSummary,
    revenueTrend,
    isLoading:
      surfaceTab.isLoading || (fetchRevenueTrend && revenueTrend.isLoading),
    isFetching:
      surfaceTab.isFetching || (fetchRevenueTrend && revenueTrend.isFetching),
    isError: surfaceTab.isError || revenueTrend.isError,
    error: surfaceTab.error ?? revenueTrend.error,
    isComparisonLoading:
      hasPreviousRange &&
      fetchRevenueTrend &&
      previousRoomUpgradeSummary.isLoading,
    isComparisonFetching:
      hasPreviousRange &&
      fetchRevenueTrend &&
      previousRoomUpgradeSummary.isFetching,
    isComparisonError: previousRoomUpgradeSummary.isError,
    comparisonError: previousRoomUpgradeSummary.error,
  };
}
